import { Drawer, IconButton, SwipeableDrawer, useMediaQuery, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { OrderDetailsView } from './OrderDetailsView';

type Props = {
  /** The order to show; null keeps the panel closed. */
  orderId: number | null;
  onClose: () => void;
};

/**
 * The order details as a panel over the list it was opened from, so picking an order never costs the
 * list its scroll position, its selected day or its filters. Phones get a bottom sheet that can be
 * swiped away, everything wider gets a drawer on the right — the same content either way, and the
 * full `/orders/:id` page stays exactly as it was for links and bookmarks.
 *
 * Neither one carries a header of its own: the details view's own title row is the panel's top row, and
 * it holds the close button.
 */
export const OrderDetailsDrawer: React.FC<Props> = ({ orderId, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

  // Handed to the details view rather than placed here: it belongs on the order title's row, which the
  // view owns, so the panel opens with content instead of with a band holding nothing but a cross.
  const closeButton = (
    <CloseButton onClick={onClose} aria-label={t('global.close')} size="small">
      <CloseIcon />
    </CloseButton>
  );

  // Mounted only once an order is picked: rendering it earlier would fetch an order nobody asked for.
  const details = orderId !== null && <OrderDetailsView orderId={orderId} variant="panel" headerAction={closeButton} />;

  if (isPhone) {
    return (
      <BottomSheet
        anchor="bottom"
        open={orderId !== null}
        onClose={onClose}
        onOpen={() => undefined}
        // The sheet is opened by tapping a card. Left on, MUI reserves a strip along the bottom edge of
        // the screen for the opening swipe, which would swallow taps on the last cards in the list.
        disableSwipeToOpen
        swipeAreaWidth={0}
      >
        {/* No drag handle above the title: the order title starts at the top edge of the sheet, and the
            close button on its row is the way out that always works. A downward drag still closes the
            sheet, though only from the top of the content — MUI leaves a drag inside a scroller that has
            been scrolled to the scroller. */}
        <Scroller>{details}</Scroller>
      </BottomSheet>
    );
  }

  return (
    <SidePanel anchor="right" open={orderId !== null} onClose={onClose}>
      <Scroller>{details}</Scroller>
    </SidePanel>
  );
};

/** The app bar's height, the same measure `Main` pushes the page content down by. */
const APP_BAR_HEIGHT = (theme: Theme) => theme.spacing(8);

/**
 * Room for the app bar's shadow, which is roughly the size of the shadow itself. The bar is fixed and
 * `TopBar` raises it to `zIndex.drawer + 1`, above both papers below, so MUI's default elevation-4 shadow
 * falls onto whatever starts at the bar's bottom edge instead of being covered by it.
 */
const APP_BAR_SHADOW = (theme: Theme) => theme.spacing(0.5);

// Both papers are flex columns with the scrolling delegated to `Scroller`, so neither ends up with a
// scrollbar of its own next to the content's.
const BottomSheet = styled(SwipeableDrawer)`
  & .MuiDrawer-paper {
    display: flex;
    flex-direction: column;
    height: calc(100dvh - ${({ theme }) => APP_BAR_HEIGHT(theme)} - ${({ theme }) => APP_BAR_SHADOW(theme)});
    overflow: hidden;
    border-radius: 16px 16px 0 0;
  }
`;

/**
 * Starts below the app bar rather than at the top of the viewport: `TopBar` raises the bar above this
 * paper, so a panel anchored at `top: 0` had the bar painted over its own header and left the order title
 * sitting in the bar's band.
 *
 * The bar's shadow is cleared with padding rather than a larger `top`, so the panel keeps the full height
 * between the bar and the bottom of the viewport and the shadow falls on the paper instead of on the
 * order title and its close button.
 */
const SidePanel = styled(Drawer)`
  & .MuiDrawer-paper {
    display: flex;
    flex-direction: column;
    /* Wide enough for the details' longest label/value rows, and never the whole screen: the strip of
       list left showing is what makes this read as a panel over the list rather than a new page. */
    width: min(640px, calc(100vw - 32px));
    top: ${({ theme }) => APP_BAR_HEIGHT(theme)};
    /* The paper is fixed, so 100% is the viewport: the panel reaches the bottom edge. */
    height: calc(100% - ${({ theme }) => APP_BAR_HEIGHT(theme)});
    padding-top: ${({ theme }) => APP_BAR_SHADOW(theme)};
    overflow: hidden;
  }
`;

// No top padding: the details view's own header supplies it, and it does so from inside the sticky box
// so the cards scroll all the way under the title rather than stopping short of it.
const Scroller = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 24px;
`;

const CloseButton = styled(IconButton)`
  && {
    flex: 0 0 auto;
    /* Cancels the button's own padding, so the cross lines up with the title's cap height and reaches
       the panel's edge instead of floating inset from both. */
    margin: -4px -8px 0 0;
  }
`;
